import { trackEvent } from '@/lib/google-analytics-init';
import { sendInternalUiAction } from './internalSink';
import type { AnalyticsEventName } from './eventNames';

/**
 * Ids and enums only — never PII (no emails, names, or addresses). Pro is
 * an admin tool handling real customer data; an email in a GA4 param is a
 * privacy incident, not a bug.
 */
export type AnalyticsEventParams = Record<string, string | number | boolean>;

/**
 * The single call site every instrumented surface uses to emit a
 * click-level analytics event (KB-2608). KB-2611's internal
 * `recordUiAction` sink (`internalSink.ts`) fans out from here — don't add
 * a second emit path. (`internalPageView.ts` is the one other caller of the
 * sink, and only because a page view must skip GA4: KB-4771.)
 *
 * GA4 and the internal sink are independent failure domains (KB-2611 AC5):
 * each call is wrapped separately, and `sendInternalUiAction` itself never
 * throws or returns a promise this function awaits, so a GA4 outage cannot
 * suppress the internal write and a `recordUiAction` failure cannot suppress
 * GA4.
 */
export function emitAnalyticsEvent(name: AnalyticsEventName, params?: AnalyticsEventParams): void {
  try {
    trackEvent(name, params);
  } catch (err) {
    console.warn('[analytics] GA4 trackEvent failed', err);
  }

  try {
    sendInternalUiAction(name, params);
  } catch (err) {
    console.warn('[analytics] internal sink failed to start', err);
  }
}
